import React, { useState } from 'react';

const DataRows = ({ row, rowIndex, handleCellEdit, handleDeleteRow, searchTerms = [] }) => {
  const [copiedManual, setCopiedManual] = useState(false);
  const [copiedSystem, setCopiedSystem] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // Track which cell is being edited

  const columns = ['Concept', 'Keywords', 'String'];
  const highlightColors = [
    'bg-orange-300', 'bg-cyan-300', 'bg-sky-300', 'bg-indigo-300',
    'bg-lightBlue-300', 'bg-teal-300', 'bg-aqua-300',
    'bg-yellow-300', 'bg-green-300', 'bg-red-300',
    'bg-purple-300', 'bg-pink-300',
    'bg-lime-300', 'bg-amber-300', 'bg-emerald-300',
    'bg-fuchsia-300', 'bg-rose-300', 'bg-violet-300'
  ];

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightText = (text, terms) => {
    if (!terms || terms.length === 0 || typeof text !== 'string' || !text) {
      return text;
    }
    const termColorMap = {};
    
    terms.forEach((term, index) => {
      termColorMap[term.toLowerCase()] = highlightColors[index % highlightColors.length];
    });
    
    let fragments = [{ text, isHighlighted: false, color: null }];
    terms.forEach(term => {
      const pattern = new RegExp(`(${escapeRegExp(term)})`, 'gi');
      const newFragments = [];
      fragments.forEach(fragment => {
        if (fragment.isHighlighted) {
          newFragments.push(fragment);
          return;
        }
        const parts = fragment.text.split(pattern);
        
        parts.forEach((part) => {
          if (!part) return;
          
          if (pattern.test(part)) {
            newFragments.push({
              text: part,
              isHighlighted: true,
              color: termColorMap[term.toLowerCase()]
            });
          } else {
            newFragments.push({
              text: part,
              isHighlighted: false,
              color: null
            });
          }
        });
      });
      fragments = newFragments;
    });

    return fragments.map((fragment, i) => {
      if (fragment.isHighlighted) {
        return (
          <span key={i} className={`${fragment.color} text-black font-medium`}>
            {fragment.text}
          </span>
        );
      }
      return <span key={i}>{fragment.text}</span>;
    });
  };

  const handleCopyToClipboard = (content, type) => {
    navigator.clipboard.writeText(content).then(() => {
      if (type === 'manual') {
        setCopiedManual(true);
        setTimeout(() => setCopiedManual(false), 2000);
      } else {
        setCopiedSystem(true);
        setTimeout(() => setCopiedSystem(false), 2000);
      }
    });
  };

  const generateSearchString = (keywords) => {
    if (!keywords) return "";
  
    const keywordList = keywords.split("/").map((keyword) => {
      keyword = keyword.trim();
      if (keyword !== '') {
        if (keyword.includes("-")) {
          const [firstKeyword, secondKeyword] = keyword.split("-");
          return `(${firstKeyword.trim()}* ADJ ${secondKeyword.trim()}*)`;
        } 
        else if (keyword.includes(" ")) {
          const [firstKeyword, secondKeyword] = keyword.split(" ");
          return `(${firstKeyword.trim()}* NEAR ${secondKeyword.trim()}*)`;
        } 
        else if (keyword.includes(".")) {
          const word = keyword.replace(".", "");
          return word;
        }
        return `${keyword}*`;
      } else {
        return "";
      }
    });
  
    const filteredKeywords = keywordList.filter(keyword => keyword !== "");
    return filteredKeywords.join(" OR ");
  };

  const SystemSearchString = generateSearchString(row["Keywords"] || "");

  const getColumnWidth = (field) => {
    switch(field) {
      case 'Concept':
        return 'w-[10%]';
      case 'Keywords':
        return 'w-[20%]';
      case 'String':
        return 'w-4/12';
      default:
        return 'w-4/12';
    }
  };

  const renderEditableCell = (field, content, isEditable = true) => {
    const cellKey = `${rowIndex}-${field}`;
    const isEditing = editingCell === cellKey;
    const hasHighlights = searchTerms.length > 0;

    return (
      <div className="relative group min-h-[6rem]">
        {isEditing ? (
          <textarea
            autoFocus
            rows="6"
            value={content}
            onChange={(e) => handleCellEdit(rowIndex, field, e.target.value)}
            onBlur={() => setEditingCell(null)}
            className="w-full h-full p-1 border border-blue-500 rounded resize-none"
          />
        ) : (
          <div
            onClick={() => isEditable && setEditingCell(cellKey)}
            className={`w-full h-full p-1 border border-gray-400 rounded whitespace-pre-wrap break-words overflow-auto min-h-[6rem] max-h-[6rem] ${
              isEditable ? 'cursor-text hover:border-blue-300' : 'bg-gray-50 cursor-default'
            }`}
          >
            {hasHighlights ? highlightText(content, searchTerms) : content}
          </div>
        )}
      </div>
    );
  };

  return (
    <tr key={rowIndex} className="border-b h-full border-gray-400">
      {columns.map((field) => (
        <td key={field} className={`border h-auto border-gray-400 ${getColumnWidth(field)}`}>
          <div className="relative group">
            {field === 'String' && (
              <button 
                onClick={() => handleCopyToClipboard(row[field] || '', 'manual')}
                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-2 hover:bg-blue-400 rounded z-10"
              >
                {copiedManual ? '✔️' : '📋'}
              </button>
            )}
            {renderEditableCell(field, row[field] || '', true)}
          </div>
        </td>
      ))}
      
      {/* System Generated String */}
      <td className="w-4/12 border h-auto border-gray-400 p-1">
        <div className="relative group">
          <button 
            onClick={() => handleCopyToClipboard(SystemSearchString, 'system')}
            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-2 hover:bg-blue-400 rounded z-10"
          >
            {copiedSystem ? '✔️' : '📋'}
          </button>
          {renderEditableCell('system', SystemSearchString, false)}
        </div>
      </td>
      
      {/* Action column */}
      <td className="w-1/12 border h-auto border-gray-400 p-1">
        <div className="flex w-full bg-white rounded-md p-0.5">
          <button
            onClick={() => handleDeleteRow(rowIndex)}
            className="p-1 w-full m-0.5 bg-red-300 hover:bg-red-600 text-red-700 font-semibold hover:text-red-100 border-red-700 hover:border-transparent rounded"
          >
            🗑️
          </button>
        </div>
      </td>
    </tr>
  );
};

export default DataRows;